import { ControlledSelectOption } from "ui";

export interface RegionConflict {
  regionId: string;
  regionName: string;
  driverName: string;
}

export interface RegionSelectOption {
  id: string;
  name: string;
  suggestedDrivers?: {
    id: string;
    name: string;
  };
}

/**
 * Detects conflicts when assigning regions to a driver
 * @param regionsData - Array of region data with suggested drivers
 * @param selectedRegionIds - Array of region IDs being assigned
 * @param currentDriverId - ID of current driver (for edit scenarios, undefined for create)
 * @param originalRegionIds - Original region assignments (for edit scenarios)
 * @returns Array of conflicts
 */
export const detectRegionConflicts = (
  regionsData: RegionSelectOption[],
  selectedRegionIds: string[],
  currentDriverId?: string,
  originalRegionIds: string[] = [],
): RegionConflict[] => {
  if (!regionsData) {
    return [];
  }

  const filteredSelectedRegionIds = selectedRegionIds.filter((id) => id !== "");

  // For edit scenarios, only check newly selected regions
  const regionIdsToCheck = currentDriverId
    ? filteredSelectedRegionIds.filter(
        (regionId) => !originalRegionIds.includes(regionId),
      )
    : filteredSelectedRegionIds;

  // Build a Map for O(1) lookups instead of O(n) find operations
  const regionsMap = new Map(regionsData.map((region) => [region.id, region]));

  const conflicts: RegionConflict[] = [];

  // Use Map lookup instead of find() for better performance
  for (const regionId of regionIdsToCheck) {
    const region = regionsMap.get(regionId);
    if (
      region?.suggestedDrivers?.id &&
      region.suggestedDrivers.id !== currentDriverId
    ) {
      conflicts.push({
        regionId: regionId,
        regionName: region.name,
        driverName: region.suggestedDrivers.name,
      });
    }
  }

  return conflicts;
};

/**
 * Converts region data to ControlledSelectOption format
 * @param regionsData - Array of region data
 * @returns Array of select options
 */
export const convertRegionsToSelectOptions = (
  regionsData: RegionSelectOption[],
): ControlledSelectOption[] => {
  return regionsData.reduce((acc: ControlledSelectOption[], region) => {
    acc.push({
      label: region.name,
      value: region.id,
    });
    return acc;
  }, [] as ControlledSelectOption[]);
};

/**
 * Generates conflict dialog message
 * @param conflicts - Array of region conflicts
 * @returns Formatted dialog message
 */
export const generateConflictDialogMessage = (
  conflicts: RegionConflict[],
): string => {
  const conflictsList = conflicts
    .map((conflict) => `• ${conflict.regionName} - ${conflict.driverName}`)
    .join("<br>");

  const conflictText =
    conflicts.length > 1
      ? "These regions already have"
      : "This region already has";

  return `${conflictText} a driver assigned:<br><br>${conflictsList}<br><br>Are you sure you want to change default driver?`;
};
